import MaterialTable, { MTableCell, MTableToolbar } from '@material-table/core'
import { Button, IconButton, Link, Menu, MenuItem, Typography, Chip, Icon, Tooltip } from '@material-ui/core'
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles'
import {
	ArrowBackIosOutlined,
	ArrowForwardIosOutlined,
	CalendarToday,
	Check,
	ExitToApp,
	Remove,
	Warning
} from '@material-ui/icons'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import { capitalize } from 'lodash-es'
import moment from 'moment'
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

import { updateFacility } from '../../actions/facilities/facilities.actions'
import { movePets } from '../../actions/pets/pets.actions'
import { getPetAge, getPetSizeV1, getDepartingInfo, getChipStyles } from '../../services/helpers'

import { tablePlayGroups } from '../../services/theme'
import { capitilizeMultipleWords, DEFAULT_EMPTY_VALUE, timeFormat } from '../../utils'
import DeleteConfirmation from '../Dialogs/DeleteConfirmation'

const useStyles = makeStyles((theme) => ({
	headerButton: {
		fontFamily: 'Nunito',
		fontWeight: '700',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	addClientButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	toolbarWrapper: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: '#F9F9F9'
	},
	petStatusIcon: {
		position: 'absolute',
		left: 0
	},
	toolbarExtras: {
		display: 'flex',
		'& > *': {
			marginRight: '16px'
		}
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		padding: '0px 12px',
		'& > div:nth-child(1)': {
			'& > div:nth-child(4)': {
				flex: 'auto'
			}
		}
	},
	datePickerContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative'
	},
	datePicker: {
		color: '#FFFFFF',
		opacity: 0,
		position: 'absolute',
		top: 0,
		height: 38,
		margin: 0,
		'& *': {
			height: 0,
			padding: 0,
			margin: 0
		}
	},
	calendarButton: {
		'& svg': {
			fill: theme.palette.primary.main
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	calendarDateButton: {
		zIndex: 1,
		border: '1px solid #D0CFD2',
		padding: '7px 16px 8px 24px',
		color: '#4D4C4F',
		fontWeight: '700',
		'& .MuiButton-label': {
			'& .MuiButton-endIcon': {
				marginLeft: 16
			},
			whiteSpace: 'nowrap'
		}
	},
	dateChangeButton: {
		border: '1px solid #D0CFD2',
		height: 38,
		width: 38,
		padding: 5,
		'& svg': {
			height: 16
		}
	},
	addAppointmentButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	tables: {
		display: 'flex',
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: 10,
		paddingRight: 15,
		paddingLeft: 15
	},
	playgroups: {
		width: '100%',
		padding: 10,
		paddingRight: 15,
		paddingLeft: 15
	},
	groupCol: {
		minHeight: 192,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		'& p': {
			margin: 0
		},
		'& .change': {
			fontFamily: 'Nunito',
			fontSize: 14,
			fontWeight: '600',
			color: '#6B4AD6',
			lineHeight: '23px',
			cursor: 'pointer'
		},
		'& .char': {
			fontFamily: 'Nunito',
			fontSize: 14,
			fontWeight: '600',
			color: '#6B4AD6',
			lineHeight: '23px',
			cursor: 'pointer',
			backgroundColor: 'rgba(107, 74, 214, 0.1)',
			padding: 4,
			paddingLeft: 6,
			paddingRight: 6,
			borderRadius: 6,
			marginRight: 4,
			marginBottom: 4
		}
	},
	groupCol2: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'flex-start',
		paddingTop: 16,
		paddingBottom: 16,
		height: 192,
		'& p': {
			margin: 0
		},
		'& .change': {
			fontFamily: 'Nunito',
			fontSize: 14,
			fontWeight: '600',
			color: '#6B4AD6',
			lineHeight: '23px',
			cursor: 'pointer'
		},
		'& .char': {
			fontFamily: 'Nunito',
			fontSize: 14,
			fontWeight: '600',
			color: '#6B4AD6',
			lineHeight: '23px',
			cursor: 'pointer',
			backgroundColor: 'rgba(107, 74, 214, 0.1)',
			padding: 4,
			paddingLeft: 6,
			paddingRight: 6,
			borderRadius: 6,
			marginRight: 4,
			marginBottom: 4
		}
	},
	timeTable: {
		width: '24%',
		backgroundColor: '#F3F3F3',
		borderRadius: 6,
		display: 'flex',
		flexDirection: 'column',
		padding: 14,
		justifyContent: 'flex-start',
		'& h4': {
			marginTop: 0,
			marginBottom: 5,
			textTransform: 'capitalize',
			fontFamily: 'Nunito',
			fontWeight: '800',
			fontSize: 16
		}
	},
	timeRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 5,
		padding: 4,
		width: '100%',
		'& span': {
			textTransform: 'capitalize',
			fontFamily: 'Nunito',
			fontWeight: '600',
			fontSize: 14
		}
	},
	timeRowLight: {
		backgroundColor: '#F9F9F9',
		borderRadius: 6
	},
	chipRow: {
		height: '100%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center'
	},
	spacer: {
		margin: '5px 16px 5px 0px',
		width: '24px',
		position: 'relative',
		'&:before': {
			content: "''",
			position: 'absolute',
			height: '1px',
			width: '100%',
			top: 'calc(50% - 1px)',
			left: 0,
			backgroundColor: '#F3F3F3'
		}
	},
	unassigned: {
		width: '100%',
		backgroundColor: '#6B4AD6',
		borderRadius: 6,
		height: 70,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: 20,
		cursor: 'pointer',
		'& h5': {
			fontFamily: 'Nunito',
			color: '#FFF',
			fontWeight: '800',
			fontSize: 16,
			margin: 0
		},
		'& i': {
			color: 'rgba(255, 255, 255, 0.6)',
			fontSize: 18
		}
	},
	group: {
		width: '100%',
		display: 'flex'

		// cursor: "pointer"
	},
	topRow: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 10
	},
	groupBody: {
		backgroundColor: '#FFFFFF',
		borderRadius: 6,
		minHeight: 192,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center'
	},
	capacity: {
		fontFamily: 'Nunito',
		fontSize: 24,
		fontWeight: '800',
		color: '#1A1A35'
	},
	kennelBlock: {
		fontFamily: 'Nunito',
		fontSize: 16,
		fontWeight: '600',
		color: '#1A1A35'
	},
	characteristics: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'flex-start'
	},
	alts: {
		width: '100%',
		fontFamily: 'Nunito',
		fontSize: 14,
		lineHeight: '19px',
		fontWeight: '600',
		color: '#1A1A35'
	},
	boxRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		'& i': {
			marginRight: 17,
			color: '#D0CFD2'
		},
		'& .checked': {
			color: '#6B4AD6'
		}
	},
	boxBox: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'center'
	},
	change: {},
	areaText: {
		fontSize: 14,
		fontWeight: '600'
	},
	areaText2: {
		fontSize: 14,
		fontWeight: '600',
		color: '#777679'
	},
	smallRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center'
	},
	smallRow2: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'flex-start',
		paddingRight: 8
	},
	char: {
		fontFamily: 'Nunito',
		fontSize: 14,
		fontWeight: '600',
		color: '#6B4AD6',
		lineHeight: '23px',
		cursor: 'pointer',
		backgroundColor: 'rgba(107, 74, 214, 0.1)',
		padding: 4,
		paddingLeft: 6,
		paddingRight: 6,
		borderRadius: 6,
		marginRight: 4,
		marginBottom: 4
	},
	location: {
		padding: 5,
		paddingLeft: 32,
		'& i': {
			marginRight: 10,
			color: '#777679'
		},
		fontFamily: 'Nunito',
		fontSize: 16,
		lineHeight: '19px',
		fontWeight: '600',
		color: '#1A1A35'
	},
	staff: {},
	staffRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		'& div': {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-start'
		},
		'& h5': {
			fontFamily: 'Nunito',
			fontSize: 16,
			lineHeight: '22px',
			fontWeight: '600',
			color: '#1A1A35',
			margin: 0,
			marginRight: '10px'
		},
		'& span': {
			fontFamily: 'Nunito',
			fontSize: 16,
			lineHeight: '22px',
			fontWeight: '600',
			color: '#777679',
			marginRight: '10px'
		}
	},
	playBlock: {
		fontFamily: 'Nunito',
		fontSize: 34,
		letterSpacing: 0.75,
		fontWeight: '800',
		color: '#1A1A35',
		overflow: 'hidden'
	},
	playBlock2: {
		fontFamily: 'Nunito',
		fontSize: 14,
		fontWeight: '600',
		color: '#1A1A35'
	},
	playName: {
		marginRight: 20,
		fontFamily: 'Nunito',
		fontSize: 16,
		fontWeight: '600',
		color: '#1A1A35',
		lineHeight: '22px'
	},
	kennelBlock: {
		fontFamily: 'Nunito',
		fontSize: 16,
		fontWeight: '600',
		color: '#777679',
		width: '50px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	wat: {},
	characteristics: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap'
	},
	chip: {
		background: 'rgba(107, 74, 214, 0.1)',
		backgroundColor: 'rgba(107, 74, 214, 0.1)',
		color: theme.palette.primary.main,
		marginRight: '4px',
		marginBottom: '4px'
	},
	title: {
		fontWeight: '800',
		fontSize: 14,
		color: '#5F38BE',
		marginBottom: 9
	},
	unassignedBox: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'flex-start',
		height: 161,
		overflowY: 'scroll',
		border: '1px solid #D0CFD2',
		borderRadius: 6,
		width: '100%',
		marginTop: 16,
		marginBottom: 12
	}
}))

export const PlaygroupTable = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const facility = useSelector((state) => state.specific.facilities.selected)
	const playgroups = (facility && facility.playgroups) || []
	// const [loading, setLoading] = useState(false)
	const [isOpen, setIsOpen] = useState(false)
	const [deactivateModal, setDeactivateModal] = useState(false)
	const [playgroupType, setPlaygroupType] = useState({})

	let {
		visits,
		date,
		setDate,
		changeDate,
		pets,
		areas,
		group,
		selected,
		selectPet,
		setIsShown,
		kennels,
		setDialogShown,
		staff,
		setPlayShown,
		loading,
		setLoading
	} = props

	// we don't need this array to rerender UI
	const visitIdsAlreadyRendered = []

	useEffect(() => {
		if (group?.playgroup_type_id && facility) {
			const { playgroup_types } = facility
			let type = playgroup_types.find((t) => t._id === group.playgroup_type_id)
			if (type) setPlaygroupType(type)
		}
	}, [group, facility])

	const getCurrentArea = (type) => {
		if (!type) {
			if (!group.area_id) return null
			let area = areas.find((a) => a._id === group.area_id)
			return area
		}
		if (type === 'indoor') {
			if (!group.default_indoor_play_area_id) return null
			let area = areas.find((a) => a._id === group.default_indoor_play_area_id)
			return area
		}
		if (type === 'outdoor') {
			if (!group.default_outdoor_play_area_id) return null
			let area = areas.find((a) => a._id === group.default_outdoor_play_area_id)
			return area
		}
	}

	const getKennelBlocks = () => {
		// this finds the kennels currently assigned to the group, regardless of the actual assigned block
		// we then show the blocks that those kennels belong to. If all of the kennels in a block are assigned to the group,
		// just show the name. Otherwise, we add the (partial) field.
		// we then add any kennels that don't have a block, but are also assigned

		let blocks = [
			...new Set(
				facility.kennels
					.filter((k) => k.playgroup_id === group._id && k.kennel_block_id && k.status !== 'disabled')
					.map((k) => k.kennel_block_id)
			)
		]
			.map((id) => facility.kennel_blocks.find((kb) => kb._id === id))
			.filter(Boolean)
		if (!blocks.length) return 'No Assignments'
		let str = ``
		let kennelsWithoutBlocks = facility.kennels.filter(
			(k) => k.playgroup_id === group._id && !k.kennel_block_id && k.status !== 'disabled'
		)

		blocks.forEach((block, i) => {
			let kennelsInBlock = facility.kennels.filter(
				(kib) => kib.kennel_block_id && kib.kennel_block_id === block._id && kib.status !== 'disabled'
			)
			let partial = kennelsInBlock.length && !kennelsInBlock.every((kib) => kib.playgroup_id === group._id)
			let toAdd = ``
			if (partial) {
				toAdd += `${block.name} (Partial)`
			} else {
				toAdd += `${block.name}`
			}

			if (i < blocks.length - 1 || kennelsWithoutBlocks.length) toAdd += ', '
			str += toAdd
		})

		kennelsWithoutBlocks.forEach((k, i) => {
			let toAdd = k.reference_number
			if (i < kennelsWithoutBlocks.length - 1) toAdd += ', '
			str += toAdd
		})
		return str
	}

	const getDefaultKennelBlock = () => {
		if (!group.kennel_block_id) return 'Unassigned'
		return facility.kennel_blocks.find((kb) => kb._id === group.kennel_block_id)?.name || 'Unassigned'
	}

	const select = (id) => {
		let pets
		if (selected.includes(id)) {
			pets = selected.filter((s) => s !== id)
		} else {
			pets = [...selected, id]
		}
		selectPet(pets)
	}

	const updateDate = (date) => {
		setDate(moment(date).format('ll'))
		setIsOpen(false)
	}

	const getCapacity = () => {
		let cap = group.capacity ? group.capacity : '?'
		let dogsInGroup = 0
		visits.forEach((visit, i) => {
			if (visit.pet_status)
				visit.pet_status.forEach((pet, i) => {
					if (pet.playgroup == group._id) dogsInGroup += 1
				})
		})

		return `${dogsInGroup} / ${cap}`
	}

	const getInFacility = () => {
		let cap = group.capacity ? group.capacity : '?'
		let dogsInGroup = 0
		visits.forEach((visit, i) => {
			if (visit.pet_status)
				visit.pet_status.forEach((pet, i) => {
					if (pet.playgroup == group._id && pet.checked_in) dogsInGroup += 1
				})
		})

		return `${dogsInGroup} in facility`
	}

	const changeKennels = () => {
		setDialogShown(true)
	}

	const changeStaff = () => {
		setIsShown(true, group)
	}

	const getKennels = (group) => {
		let str = ``

		let ourKens = group.kennels || []

		ourKens.forEach((kennel, i) => {
			if (i === ourKens.length - 1) {
				str += `${kennel}`
			} else {
				str += `${kennel}, `
			}
		})

		return str
	}

	const getPlayArea = (group) => {
		if (!group) return 'Unassigned'
		let area = areas.find((a) => a._id === group.area_id)
		return area ? area.name : 'Unassigned'
	}

	const changePlayArea = (group) => {
		setPlayShown(true)
	}

	const getOutliers = () => {
		if (!playgroupType?._id) return null

		const dogs = []
		const outliers = []
		visits.forEach((visit, i) => {
			if (visit.pet_status)
				visit.pet_status.forEach((pet, i) => {
					if (pet.playgroup == group._id) dogs.push(pet)
				})
		})

		dogs.map((dog) => {
			let wholeDog = pets.find((p) => p._id === dog.pet_id)
			return wholeDog
		})
			.filter(Boolean)
			.forEach((dog, i) => {
				let size = getPetSizeV1(dog)

				let age = dog.birthday ? getPetAge(dog.birthday) : null
				age = age ? age.toLowerCase() : ''

				if (
					size &&
					playgroupType.best_fit_sizes &&
					!playgroupType.best_fit_sizes.includes(size) &&
					!playgroupType.best_fit_sizes.includes('all')
				)
					outliers.push(size.toUpperCase())
				if (dog.play_style && playgroupType.play_style && playgroupType.play_style !== dog.play_style)
					outliers.push(dog.play_style)
				if (
					playgroupType.age_category &&
					playgroupType.age_category.toLowerCase() !== 'all' &&
					age &&
					!playgroupType.age_category !== age
				)
					outliers.push(age)
			})

		const outs = [...new Set(outliers)]

		const text = outs.length ? outs.map((w) => capitilizeMultipleWords(w)).join(', ') : DEFAULT_EMPTY_VALUE

		return (
			<div className={classes.playBlock}>
				<Typography
					style={{
						// marginRight: 12,
						// marginBottom: 6,
						color: outliers.length ? '#DB4A42' : '#777679',
						fontSize: '14px',
						fontWeight: '600',
						lineHeight: '19px'
					}}>
					{text}
				</Typography>
			</div>
		)
	}

	const changePlaygroupStatus = () => {
		setLoading(true)
		let status = group.status === 'overflow' ? 'in use' : 'overflow'

		// move the pets to 'unassigned', if we are deactivating the group
		if (status === 'overflow') {
			let ourVisits = []
			let pet_ids = pets.map((p) => p._id)
			visits.forEach((v) => {
				if (v.pet_status) {
					v.pet_status.forEach((stat, i) => {
						if (stat.playgroup === group._id) ourVisits.push(v._id)
					})
				}
			})

			let payload = {
				pet_ids,
				playgroup: null,
				visit_ids: ourVisits
			}
			if (pet_ids?.length && ourVisits?.length) dispatch(movePets(payload))
		}

		let playgroups = facility.playgroups.map((g) => {
			let area_id
			if (g._id === group._id) {
				if (status === 'overflow') {
					area_id = null
				} else {
					area_id = g.default_indoor_play_area_id
				}
			} else {
				area_id = g.area_id
			}
			return {
				...g,
				status: g._id === group._id ? status : g.status,
				area_id
			}
		})

		let kennels
		// we are deactivating the group, so we will unassign any kennels that
		// it currently has assigned
		if (status === 'overflow') {
			kennels = facility.kennels.map((k) => {
				return {
					...k,
					playgroup_id: k.playgroup_id === group._id ? null : k.playgroup_id
				}
			})
		} else {
			// we are activating the group, so go find any kennels that are in its default kennel block
			// and assign those to the group (unless they are assigned elsewhere)
			kennels = facility.kennels.map((k) => {
				return {
					...k,
					playgroup_id:
						group.kennel_block_id && k.kennel_block_id === group.kennel_block_id && !k.playgroup_id
							? group._id
							: k.playgroup_id
				}
			})
		}

		let fac = {
			...facility,
			playgroups,
			kennels
		}
		dispatch(updateFacility(facility._id, fac))

		setLoading(false)
		setDeactivateModal(false)
	}

	const getModalText = () => {
		if (group.status === 'overflow') {
			// we are activating the group, so use these...
			if (group.default_status === 'in use')
				return 'Are you sure you want to activate this group? It will remain active until manually deactivated.'
			return 'Are you sure you want to activate this group? It will remain active for today, but will deactivate tomorrow morning if there are no dogs in the group'
		} else {
			// we are deactivating
			if (group.default_status === 'in use') {
				if (pets && pets.length)
					return `Are you sure you want to deactivate this playgroup for today? There ${
						pets.length > 1 ? 'are' : 'is'
					} ${pets.length} dog${
						pets.length > 1 ? 's' : ''
					} assigned to this group. If deactivated, they will be moved to Unassigned Dogs.`
				return 'Are you sure you want to deactivate this playgroup for today? It will only remain deactivated for the day, and then reactivate tomorrow morning.'
			} else {
				if (pets && pets.length)
					return `Are you sure you want to deactivate this playgroup? There ${
						pets.length > 1 ? 'are' : 'is'
					} ${pets.length} dog${
						pets.length > 1 ? 's' : ''
					} assigned to this group. If deactivated, they will be moved to Unassigned Dogs.`
				return 'Are you sure you want to deactivate this playgroup? It will remain deactivated until manually re-activated.'
			}
		}
	}

	const getAssignedDogs = () => {
		if (pets && pets.length)
			return (
				<div className={classes.unassignedBox}>
					{pets.map((p, index) => (
						<Typography
							variant="body1"
							style={{
								marginLeft: 28,
								marginTop: index > 0 ? 16 : 0,
								fontSize: 16,
								fontWeight: '600'
							}}>
							{p.name}
						</Typography>
					))}
				</div>
			)

		return null
	}

	const getPlaygroup = () => {
		const currentArea = getCurrentArea()
		const defaultArea = getCurrentArea('indoor')
		const outdoorArea = getCurrentArea('outdoor')
		const blocks = getKennelBlocks()

		let blockColor = blocks === 'No Assignments' ? '#DB4A42' : '#777679'
		let areaColor = currentArea ? '#777679' : '#DB4A42'
		if (group.status !== 'in use') {
			blockColor = '#777679'
			areaColor = '#777679'
		}

		return (
			<div container spacing={3} style={{ position: 'relative', width: '100%' }}>
				<MenuThing setDeactivateModal={setDeactivateModal} group={group} />
				<DeleteConfirmation
					isShown={deactivateModal}
					setIsShown={() => setDeactivateModal(false)}
					onConfirmDelete={changePlaygroupStatus}
					isDeleteLoading={loading}
					confirmButtonText={group.status === 'overflow' ? 'Activate' : 'Deactivate'}
					title={group.status === 'overflow' ? 'Activate Playgroup' : 'Deactivate Playgroup'}
					body={getModalText()}>
					{getAssignedDogs()}
				</DeleteConfirmation>
				<div className={classes.groupBody}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'flex-start',
							paddingLeft: 34,
							width: '17%'
						}}>
						<Typography style={{ fontSize: 22, fontWeight: '800' }}>{group.name}</Typography>
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'flex-start',
								alignItems: 'center',
								marginRight: 9
							}}>
							<i
								className="fa fa-circle"
								style={{
									color: group.status === 'in use' ? '#007a4b' : '#DB4A42',
									marginRight: 6,
									fontSize: 12
								}}
							/>
							<Typography
								variant="body1"
								style={{
									color: group.status === 'in use' ? '#007a4b' : '#DB4A42',
									fontSize: 14
								}}>
								{group.status === 'in use' ? 'Activated' : 'Deactivated'}
							</Typography>
						</div>
					</div>

					<div className={classes.groupCol2} style={{ width: '25%', paddingLeft: 25 }}>
						<Typography variant="overline" className={classes.title}>
							TOTAL CAPACITY
						</Typography>
						<div className={classes.playBlock}>{getCapacity()}</div>
						<div className={classes.playBlock2}>{getInFacility()}</div>
					</div>

					<div className={classes.groupCol2} style={{ paddingRight: 12, width: '38%' }}>
						<Typography variant="overline" className={classes.title} style={{ marginBottom: -5 }}>
							ASSIGNMENTS{' '}
							{(!currentArea || blocks === 'No Assignments') && group.status === 'in use' ? (
								<i className="fas fa-exclamation-circle" style={{ color: '#DB4A42' }}></i>
							) : null}
						</Typography>
						<div className={classes.smallRow}>
							<Typography className={classes.areaText} style={{ marginRight: 16 }}>
								Play
							</Typography>
							<Typography
								className={classes.areaText2}
								style={{ marginRight: 16, color: areaColor }}>
								{currentArea?.name || 'No Assignment'}
							</Typography>
							<Button
								variant="text"
								onClick={setPlayShown}
								style={{
									fontSize: 14,
									fontWeight: 600,
									color: '#6B4AD6',
									textTransform: 'capitalize',
									padding: 0
								}}>
								Change
							</Button>
						</div>
						<Typography className={classes.areaText} style={{ marginRight: 16, fontSize: 12 }}>
							Default Indoor - {defaultArea?.name || 'No Assignment'}
						</Typography>
						<Typography className={classes.areaText} style={{ marginRight: 16, fontSize: 12 }}>
							Default Outdoor - {outdoorArea?.name || 'No Assignment'}
						</Typography>
						<div className={classes.smallRow2} style={{ marginTop: 12 }}>
							<Typography className={classes.areaText} style={{ marginRight: 16, marginTop: 1 }}>
								Rest
							</Typography>
							<Typography
								className={classes.areaText2}
								style={{
									marginRight: 16,
									marginTop: 1,
									color: blockColor
								}}>
								{blocks}
							</Typography>
							<Button
								variant="text"
								onClick={setDialogShown}
								style={{
									fontSize: 14,
									fontWeight: 600,
									color: '#6B4AD6',
									textTransform: 'capitalize',
									padding: 0
								}}>
								Change
							</Button>
						</div>
						<Typography className={classes.areaText} style={{ marginRight: 16, fontSize: 12 }}>
							Default Block - {getDefaultKennelBlock()}
						</Typography>
					</div>

					<div className={classes.groupCol2} style={{ width: '25%' }} xs={4}>
						<Typography variant="overline" className={classes.title}>
							CHARACTERISTICS
						</Typography>
						<div className={classes.smallCol}>
							<Typography
								className={classes.areaText}
								style={{ marginRight: 12, textTransform: 'capitalize', marginBottom: 6 }}>
								Play Style:{' '}
								{capitilizeMultipleWords(playgroupType?.play_style) || DEFAULT_EMPTY_VALUE}
							</Typography>
							<Typography className={classes.areaText} style={{ marginRight: 12, marginBottom: 6 }}>
								Size:{' '}
								{playgroupType?.best_fit_sizes?.map((j) => j.toUpperCase()).join(', ') ||
									DEFAULT_EMPTY_VALUE}
							</Typography>
							<Typography
								className={classes.areaText}
								style={{ marginRight: 12, textTransform: 'capitalize', marginBottom: 6 }}>
								Age: {capitalize(playgroupType?.age_category) || DEFAULT_EMPTY_VALUE}
							</Typography>
						</div>
					</div>

					<div className={classes.groupCol2} style={{ width: '25%' }}>
						<Typography variant="overline" className={classes.title}>
							OUTLIERS
						</Typography>
						{getOutliers()}
					</div>
				</div>
			</div>
		)
	}

	const getPetName = (pet) => {
		if (pet) {
			let visit = visits.find((v) => v.pet_ids.includes(pet._id))

			let name = pet.name
			let primary

			if (visit && visit.household && visit.household.accounts && visit.household.accounts.length) {
				primary = visit.household.accounts.find((a) => a.primary)

				primary = primary ? primary : visit.household.accounts[0]

				name = `${name}${primary && primary.last_name ? ` ${primary.last_name}` : ''}`
			}
			return name
		}
	}

	const renderPet = (pet) => {
		if (pet) {
			let location
			let visit = visits.find((v) => v.pet_ids.includes(pet._id))
			let status = visit && visit.pet_status ? visit.pet_status.find((s) => s.pet_id === pet._id) : null

			let day_kennel = kennels
				.filter((k) => k.pet_ids?.includes(pet._id))
				.map((k) => k.reference_number)
				.join(', ')
			if (!day_kennel) day_kennel = '-'

			if (status) {
				if (status.location === 'playing') {
					// find the playgroup area id
					if (!group || !group.area_id) {
						location = 'Playing - no area'
					} else {
						let area = areas.find((a) => a._id === group.area_id)
						location = area ? area.name : ''
					}
				} else if (status.location === 'resting') {
					// find the kennel and kennel area id
					let kennel = kennels.find((k) => k?.pet_ids?.includes(pet._id))
					if (!kennel) location = 'Resting - No Kennel'
					let area_id = kennel ? kennel.area_id : ''
					let area = areas.find((a) => a._id === area_id)
					location = `${area ? `${area.name}: ` : ''} ${kennel ? kennel.reference_number : ''}`
				} else {
					// just show whatever the location is marked as
					if (status.checked_in) {
						location = status.location ? status.location : 'In Facility'
					} else {
						location = 'Offsite'
					}
				}
			} else {
				location = 'Offsite'
			}

			let name = getPetName(pet)

			return (
				<div>
					<div className={classes.boxRow}>
						<i
							className={selected.includes(pet._id) ? 'fa fa-check-square checked' : 'far fa-square'}
						/>
						<Link
							component={RouterLink}
							style={{
								textTransform: 'capitalize',
								color: '#6B4AD6',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: '22px'
								// whiteSpace: 'nowrap',
								// overflow: 'hidden',
								// textOverflow: 'ellipsis'
							}}
							onClick={(e) => e.stopPropagation()}
							to={`/clients/${pet.household_id}/pets/${pet._id}`}>
							{name}
						</Link>
					</div>
					<div className={classes.location}>
						<i className="fa fa-map-marker-alt" />
						{location}
					</div>
					<div className={classes.location}>
						<i className="fa fa-map-marker-alt" />
						Day Kennel: {day_kennel}
					</div>
				</div>
			)
		}
	}

	const renderBestFit = (pet) => {
		let text = '---'
		let { playgroup_types } = facility
		if (playgroup_types && pet.best_fit_playgroup_type) {
			let group = playgroup_types.find((t) => t._id === pet.best_fit_playgroup_type)
			if (group) text = group.name
		}
		return (
			<Typography variant="body1" style={{ fontSize: 14 }}>
				{text}
			</Typography>
		)
	}

	const renderAlternate = (pet) => {
		let text = '---'
		let { playgroup_types } = facility
		if (playgroup_types && pet.alt_playgroup_type) {
			let group = playgroup_types.find((t) => t._id === pet.alt_playgroup_type)
			if (group) text = group.name
		}

		return (
			<Typography variant="body1" style={{ fontSize: 14 }}>
				{text}
			</Typography>
		)
	}

	const getFlexString = (pet) => {
		let text = ''
		let { playgroup_types } = facility
		if (playgroup_types && pet.other_playgroup_types) {
			let groups = pet.other_playgroup_types
				.map((type, i) => {
					let group = playgroup_types.find((t) => t._id === type)
					return group?.name || ''
				})
				.filter(Boolean)

			groups.sort()
			if (groups.length) text = groups[0]
		}

		return text || ''
	}

	const getBestFitString = (pet) => {
		let text = ''
		let { playgroup_types } = facility
		if (playgroup_types && pet.best_fit_playgroup_type) {
			let group = playgroup_types.find((t) => t._id === pet.best_fit_playgroup_type)
			text = group?.name || ''
		}

		return text || ''
	}

	const getAltString = (pet) => {
		let text = ''
		let { playgroup_types } = facility
		if (playgroup_types && pet.alt_playgroup_type) {
			let group = playgroup_types.find((t) => t._id === pet.alt_playgroup_type)
			text = group?.name || ''
		}

		return text || ''
	}

	const renderFlex = (pet) => {
		let text = '---'
		let { playgroup_types } = facility
		if (playgroup_types && pet.other_playgroup_types) {
			text = ''
			pet.other_playgroup_types.forEach((type, i) => {
				let group = playgroup_types.find((t) => t._id === type)
				if (group && i < pet.other_playgroup_types.length - 1) {
					text += `${group.name}, `
				} else if (group) {
					text += `${group.name}`
				}
			})
		}

		return (
			<Typography variant="body1" style={{ fontSize: 14 }}>
				{text || '---'}
			</Typography>
		)
	}

	const renderToolTip = (issues) => {
		let style = getChipStyles('rejected')

		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<ToolTipItem message="Dog Beef" color={style?.color || null} />
			</div>
		)
	}

	const ToolTipItem = (props) => {
		let { message, color } = props
		return (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<div
					style={{
						height: '9px',
						width: '9px',
						backgroundColor: `${color ? color : '#EB4C4C'}`,
						borderRadius: '50%',
						display: 'inline-block',
						marginRight: '16px'
					}}
				/>
				<Typography style={{ fontWeight: 600, fontSize: '14px' }}>{message}</Typography>
			</div>
		)
	}

	const renderCharacteristics = (pet) => {
		let size = getPetSizeV1(pet)
		let age = pet.birthday ? getPetAge(pet.birthday) : null

		const hasBeefWithDog = pet?.beefs?.length && pet.beefs.some((p) => !!pets.find((d) => d._id === p._id))

		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-start',
					alignItems: 'center'
				}}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start',
						alignItems: 'flex-start'
					}}>
					<Typography variant="body1" style={{ fontSize: 14, textTransform: 'capitalize' }}>
						PS: {pet.play_style || '---'}
					</Typography>
					<Typography variant="body1" style={{ fontSize: 14, textTransform: 'uppercase' }}>
						S: {size || '---'}
					</Typography>
					<Typography variant="body1" style={{ fontSize: 14 }}>
						A: {age || '---'}
					</Typography>
				</div>
				<div style={{ paddingLeft: 16 }}>
					{hasBeefWithDog && (
						<Tooltip title={renderToolTip()}>
							<Chip
								label={<Warning style={{ fontSize: '15px' }} />}
								style={{
									...getChipStyles('rejected'),
									...{ paddingTop: '6px' }
								}}
							/>
						</Tooltip>
					)}
				</div>
			</div>
		)
	}

	const renderCubbies = (data) => {
		if (data && data.length) {
			return (
				<Typography variant="body1" style={{ fontSize: 14, width: '130px' }}>
					<span>{data.join('-')}</span>
				</Typography>
			)
		}
	}

	const getCompatText = (visit) => {
		if (visit && visit.pet_status && visit.pet_status.find((p) => p.checked_in)) return 'Departing'
		return 'Arriving'
	}

	const renderDetails = (data) => {
		let everyPetHasStatus = data.pet_ids.every((id) => data.pet_status.find((s) => s.pet_id === id))
		let hasArrivingPets =
			data.status && data.status.id && data.status.id > 4 && (data.status.id > 4 || data.status.id === 5)
				? false
				: !everyPetHasStatus ||
				  (everyPetHasStatus &&
						moment(data.startDate).isSame(date, 'date') &&
						data.pet_status.some((s) => !s.checked_in && !s.checked_out))

		let headingText = ''
		let timeText = ''

		let dropOffBegin =
			data.type === 'boarding' && !data.first_day_daycare
				? '17:00'
				: facility?.services?.daycare?.dropoff_begins
		let dropOffDeadline =
			data.type === 'boarding' && !data.first_day_daycare
				? '21:00'
				: facility?.services?.daycare?.dropoff_deadline
		let pickUpDeadline =
			data.type === 'boarding' && !data.last_day_daycare
				? facility?.services?.boarding?.pickup_deadline
				: facility?.services?.daycare?.pickup_deadline

		if (hasArrivingPets) {
			headingText = 'Arriving'
			if (data.early_dropoff) {
				dropOffBegin = '06:00'
				dropOffDeadline = '07:00'
			} else if (data.time_block === 'afternoon') {
				dropOffBegin = '12:00'
				dropOffDeadline = '14:00'
			} else if (data.time_block === 'evening') {
				dropOffBegin = '18:30'
				dropOffDeadline = '19:30'
			}
			timeText =
				dropOffBegin && dropOffDeadline
					? `${moment(dropOffBegin, 'HH:mm').format(timeFormat(dropOffBegin))} - ${moment(
							dropOffDeadline,
							'HH:mm'
					  ).format(timeFormat(dropOffDeadline))}`
					: ''
		}

		const prms = {
			headingText,
			timeText,
			date,
			pickUpDeadline
		}

		;({ timeText, headingText } = getDepartingInfo(data, prms))

		return (
			<Fragment>
				{headingText ? <Typography variant="caption">{headingText}</Typography> : null}
				{timeText ? (
					<Typography variant="caption" style={{ color: '#777679', display: 'block' }}>
						{timeText}
					</Typography>
				) : null}
			</Fragment>
		)
	}

	const handleAppRightClick = (e, prms) => {
		e.preventDefault()
		const url = `/clients/${prms.household_id}/appointments/${prms._id}${`?date=${prms.date}`}`
		const win = window.open(url, '_blank')
		win.focus()
	}

	const renderAppointment = (data) => {
		let visit = visits.find(
			(v) =>
				v.pet_ids.includes(data._id) && v.type !== 'grooming' && !visitIdsAlreadyRendered.includes(v._id)
		)
		if (!visit) return null
		visitIdsAlreadyRendered.push(visit._id)
		const formattedDate = moment(date).format('YYYYMMDD')
		const appPrms = {
			_id: visit._id,
			household_id: visit.household_id,
			date: formattedDate
		}
		return (
			<Fragment>
				<Typography
					onContextMenu={(e) => handleAppRightClick(e, appPrms)}
					variant="caption"
					style={{ textTransform: 'capitalize' }}>
					{visit.type === 'compatibility_session' ? 'Compatibility Session' : visit.type}
				</Typography>
				<Typography variant="caption" style={{ color: '#777679', display: 'block' }}>
					{renderDetails(visit)}
				</Typography>
			</Fragment>
		)
	}

	return !group ? null : (
		<div className={classes.page}>
			<MuiThemeProvider theme={tablePlayGroups}>
				<MaterialTable
					scheduleAggregates={props.scheduleAggregates}
					date={props.date}
					options={{
						headerStyle: {
							color: '#1A1A35',
							textTransform: 'uppercase',
							fontWeight: 800,
							fontSize: '12px',
							lineHeight: '16px',
							padding: '11px 24px',
							borderTop: '1px solid #D0CFD2',
							backgroundColor: '#F9F9F9'
						},
						rowStyle: {
							verticalAlign: 'top',
							fontSize: 16
						},
						pageSize: 20,
						pageSizeOptions: [10, 20, 50],
						toolbarButtonAlignment: 'left',
						searchFieldAlignment: 'left',
						searchFieldVariant: 'filled'
					}}
					columns={[
						{
							title: 'PET',
							render: (data) => renderPet(data),
							customFilterAndSearch: (value, rowData) => {
								if (!value) return true
								return rowData.name.toLowerCase().includes(value.toLowerCase())
							},
							customSort: (a, b) => {
								return getPetName(a).localeCompare(getPetName(b))
							},
							width: 250,
							cellStyle: {
								paddingLeft: 24,
								paddingTop: 24,
								paddingRight: 0,
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'flex-start',
								flexDirection: 'column'
							}
						},
						{
							title: 'Appointment',
							field: 'type',
							width: 170,
							render: (data) => renderAppointment(data),
							customSort: (a, b) => {
								let visitA = visits.find((v) => v.pet_ids.includes(a._id))
								let visitB = visits.find((v) => v.pet_ids.includes(b._id))

								return visitA.type.localeCompare(visitB.type)
							}
						},
						{
							title: 'Cubbies',
							sorting: false,
							render: (data) => {
								const { cubbies: petCubbies = {} } = data

								const {
									gear: gearFac,
									food: foodFac,
									boarding: boardingFac,
									medication: medicationFAc
								} = facility?.cubbies || {}

								const cubbies = []

								for (const petCubby in petCubbies) {
									if (Object.hasOwn(petCubbies, petCubby)) {
										let prefix = ''
										if (petCubby === 'gear') {
											prefix = gearFac?.prefix || 'G'
										} else if (petCubby === 'food') {
											prefix = foodFac?.prefix || 'F'
										} else if (petCubby === 'medication') {
											prefix = medicationFAc?.prefix || 'M'
										} else if (petCubby === 'boarding') {
											prefix = boardingFac?.prefix || 'B'
										} else {
											continue
										}
										const cubbyNum = petCubbies[petCubby]
										if (cubbyNum !== null) {
											cubbies.push(`${prefix}${petCubbies[petCubby]}`)
										}
									}
								}

								return cubbies.length > 0 ? renderCubbies(cubbies) : 'N/A'
							}
						},
						{
							title: 'BEST FIT',
							width: 170,
							render: (data) => renderBestFit(data),
							customSort: (a, b) => {
								return getBestFitString(a).localeCompare(getBestFitString(b))
							}
						},
						{
							title: 'ALTERNATE',
							width: 170,
							render: (data) => renderAlternate(data),
							customSort: (a, b) => {
								return getAltString(a).localeCompare(getAltString(b))
							}
						},
						{
							title: 'FLEX',
							width: 170,
							render: (data) => renderFlex(data),
							customSort: (a, b) => {
								return getFlexString(a).localeCompare(getFlexString(b))
							}
						},
						{
							title: 'CHARACTERISTICS dude',
							width: 450,
							sorting: false,
							render: (data) => renderCharacteristics(data)
						}
					]}
					data={pets}
					onRowClick={(e, pet) => select(pet._id)}
					components={{
						Cell: (props) => (
							<MTableCell
								style={{
									borderBottom: 'none',
									padding: '16px 36px 16px 24px'
								}}
								{...props}
							/>
						),
						Toolbar: (props) => (
							<div className={classes.toolbarWrapper}>
								<div className={classes.toolbar}>
									<MTableToolbar {...props} />
									<div className={classes.toolbarExtras}>
										<div className={classes.datePickerContainer}>
											<Button
												className={clsx(classes.headerButton, classes.calendarDateButton)}
												onClick={() => setIsOpen(true)}
												variant="outlined"
												endIcon={
													<CalendarToday
														style={{ height: 19, width: 19, fill: '#777679' }}
													/>
												}
												size="large">
												{date}
											</Button>
											<KeyboardDatePicker
												disableToolbar
												open={isOpen}
												onOpen={() => setIsOpen(true)}
												onClose={() => setIsOpen(false)}
												variant="inline"
												format="MM/DD/YYYY"
												margin="normal"
												id="start-date"
												label="Start Date"
												inputVariant="filled"
												value={date}
												onChange={updateDate}
												className={classes.datePicker}
												rightArrowIcon={
													<ArrowForwardIosOutlined
														className={classes.calendarHeaderButtons}
													/>
												}
												leftArrowIcon={
													<ArrowBackIosOutlined
														className={classes.calendarHeaderButtons}
													/>
												}
												KeyboardButtonProps={{
													'aria-label': 'Start Date',
													className: classes.calendarButton
												}}
											/>
										</div>
										<IconButton
											className={classes.dateChangeButton}
											onClick={() => changeDate('subtract')}>
											<ArrowBackIosOutlined style={{ fill: '#777679' }} />
										</IconButton>
										<IconButton
											className={classes.dateChangeButton}
											onClick={() => changeDate('add')}>
											<ArrowForwardIosOutlined style={{ fill: '#777679' }} />
										</IconButton>
									</div>
								</div>
								<div className={classes.playgroups}>{getPlaygroup()}</div>
							</div>
						)
					}}
					title={group.name}
				/>
			</MuiThemeProvider>
		</div>
	)
}

const MenuThing = ({ setDeactivateModal, group }) => {
	const [anchorEl, setAnchorEl] = React.useState(null)

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	return (
		<React.Fragment>
			<IconButton
				aria-controls="simple-menu"
				aria-haspopup="true"
				style={{
					position: 'absolute',
					top: 12,
					right: 12
				}}
				onClick={handleClick}>
				<MoreHorizIcon style={{ fill: '#777679' }} />
			</IconButton>
			<Menu
				id="simple-menu"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				transformOrigin={{ vertical: 'top', horizontal: 'center' }}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}>
				<MenuItem onClick={() => setDeactivateModal(true)} style={{ color: '#DB4A42' }}>
					{group.status === 'overflow' ? 'Activate' : 'Deactivate'} Playgroup For Today
				</MenuItem>
			</Menu>
		</React.Fragment>
	)
}
